import { NavLink, Route, Routes } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { BrandProvider, brandQuery, useBrand } from "./brandContext";
import { ScanProvider, useScanActivity } from "./scanContext";
import { ToastProvider, useToast } from "./toastContext";
import { OverviewPage } from "./pages/Overview";
import { MentionsPage } from "./pages/Mentions";
import { CopiesPage } from "./pages/Copies";
import { ParsersPage } from "./pages/Parsers";
import { BrandsPage } from "./pages/Brands";
import { ImagesPage } from "./pages/Images";
import { Select } from "./components/Select";
import { NavIcon } from "./components/NavIcon";
import { LoginPage } from "./pages/Login";

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
    ...init,
  });
  if (!res.ok) throw new Error(`${res.status} ${path}`);
  return res.json() as Promise<T>;
}

function Shell({ user, onLogout }: { user: string; onLogout: () => void }) {
  const { brands, activeId, activate } = useBrand();
  const [needsHuman, setNeedsHuman] = useState(0);
  const [lastScan, setLastScan] = useState<string | null>(null);
  const [theme, setTheme] = useState<"dark" | "light">(() => {
    const saved = localStorage.getItem("vkourse-theme");
    return saved === "light" ? "light" : "dark";
  });

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("vkourse-theme", theme);
  }, [theme]);

  useEffect(() => {
    void (async () => {
      try {
        const [plugins, st] = await Promise.all([
          api<{ items: { needsHuman: boolean }[] }>("/api/plugins"),
          api<{ lastScanAt: string | null }>(`/api/stats${brandQuery(activeId)}`),
        ]);
        setNeedsHuman(plugins.items.filter((p) => p.needsHuman).length);
        setLastScan(st.lastScanAt);
      } catch {
        /* ignore */
      }
    })();
  }, [activeId]);

  const active = brands.find((b) => b.id === activeId);
  const updatedLabel = lastScan
    ? `Данные: ${new Date(lastScan).toLocaleString("ru-RU")}`
    : "Нет сканирований — нажмите «Скан»";

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand-mark">
          <div className="radar-dot" />
          <div>
            <h1>ВКУРСЕ</h1>
            <small>МОНИТОРИНГ БРЕНДА</small>
          </div>
        </div>

        <Select
          label="Проект"
          value={activeId ? String(activeId) : ""}
          placeholder="Выберите бренд"
          options={brands.map((b) => ({
            value: String(b.id),
            label: b.name,
            hint: b.queries?.[0],
          }))}
          onChange={(v) => {
            const id = Number(v);
            if (id) void activate(id);
          }}
        />

        <nav className="nav">
          <NavLink to="/" end>
            <NavIcon name="overview" /> Обзор
          </NavLink>
          <NavLink to="/mentions">
            <NavIcon name="mentions" /> Упоминания
          </NavLink>
          <NavLink to="/watch">
            <NavIcon name="watch" /> Отслеживаемые
          </NavLink>
          <NavLink to="/copies">
            <NavIcon name="copies" /> Копии
          </NavLink>
          <NavLink to="/images">
            <NavIcon name="images" /> Логотип
          </NavLink>
          <NavLink to="/brands">
            <NavIcon name="brands" /> Бренды
          </NavLink>
          <NavLink to="/parsers">
            <NavIcon name="parsers" /> Парсеры
            {needsHuman > 0 ? <span className="badge" title="Нужен человек" /> : null}
          </NavLink>
        </nav>

        <div className="sidebar-foot">
          <div className="sys-ok">
            <span className="pulse" />
            <div>
              <div className="sys-ok-title">{active ? active.name : "Система активна"}</div>
              <div className="sys-ok-sub">
                {user} · live{" "}
                <button type="button" className="linkish" onClick={onLogout}>
                  выйти
                </button>
              </div>
            </div>
          </div>
          <div className="theme-toggle" role="group" aria-label="Тема">
            <button
              type="button"
              className={theme === "dark" ? "active" : ""}
              onClick={() => setTheme("dark")}
            >
              Тёмная
            </button>
            <button
              type="button"
              className={theme === "light" ? "active" : ""}
              onClick={() => setTheme("light")}
            >
              Светлая
            </button>
          </div>
        </div>
      </aside>
      <div className="main">
        <header className="topbar">
          <div className="status">
            <span className="dot" />
            {updatedLabel}
          </div>
          <ScanTicker />
          <ScanButton
            brandId={activeId}
            onDone={(ts) => {
              setLastScan(ts);
              void api<{ items: { needsHuman: boolean }[] }>("/api/plugins").then((p) =>
                setNeedsHuman(p.items.filter((x) => x.needsHuman).length),
              );
            }}
          />
        </header>
        <ScanProgressBar />
        <div className="content">
          <Routes>
            <Route path="/" element={<OverviewPage />} />
            <Route path="/mentions" element={<MentionsPage />} />
            <Route path="/watch" element={<MentionsPage initialFilter="watch" />} />
            <Route path="/copies" element={<CopiesPage />} />
            <Route path="/images" element={<ImagesPage />} />
            <Route path="/brands" element={<BrandsPage />} />
            <Route path="/parsers" element={<ParsersPage />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

function ScanTicker() {
  const { scanning, progressLabel } = useScanActivity();
  if (!scanning) return null;
  return (
    <div className="scan-ticker" role="status">
      {progressLabel || "Сканирование…"}
    </div>
  );
}

function ScanProgressBar() {
  const { scanning, progressPct } = useScanActivity();
  if (!scanning) return null;
  return (
    <div className="scan-progress">
      <div className="scan-progress-fill" style={{ width: `${progressPct}%` }} />
    </div>
  );
}

function ScanButton({
  brandId,
  onDone,
}: {
  brandId: number | null;
  onDone: (ts: string) => void;
}) {
  const { scanning, progressPct, beginScan, endScan } = useScanActivity();
  const { toast } = useToast();
  return (
    <button
      className="btn primary"
      disabled={scanning || !brandId}
      title={!brandId ? "Сначала выберите бренд" : undefined}
      onClick={() => {
        void (async () => {
          beginScan();
          try {
            const res = await api<{
              fetched: number;
              high: number;
              imageMatched?: number;
              imageChecked?: number;
            }>("/api/scan", {
              method: "POST",
              body: JSON.stringify({ brandId }),
            });
            onDone(new Date().toISOString());
            const img =
              res.imageMatched != null
                ? `, фото-совпадений ${res.imageMatched}/${res.imageChecked || 0}`
                : "";
            toast(
              `Скан завершён: ${res.fetched} хитов, high ${res.high}${img}`,
              "ok",
            );
          } catch (e) {
            const msg = e instanceof Error ? e.message : String(e);
            toast(
              msg.includes("409")
                ? "Скан уже идёт (таймер или другая вкладка)"
                : `Ошибка скана: ${msg}`,
              "err",
            );
          } finally {
            endScan();
          }
        })();
      }}
    >
      {scanning ? `Скан ${progressPct}%` : "Скан"}
    </button>
  );
}

export function App() {
  const [user, setUser] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    void (async () => {
      try {
        const r = await fetch("/api/auth/me");
        if (r.ok) {
          const d = (await r.json()) as { user: string };
          setUser(d.user);
        }
      } catch {
        /* ignore */
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  const onLogin = useCallback((u: string) => setUser(u), []);
  const onLogout = useCallback(() => {
    void fetch("/api/auth/logout", { method: "POST" }).finally(() =>
      setUser(null),
    );
  }, []);

  if (checking) {
    return <div className="boot-splash" aria-label="Загрузка" />;
  }
  if (!user) {
    return <LoginPage onSuccess={onLogin} />;
  }
  return (
    <BrandProvider>
      <ScanProvider>
        <ToastProvider>
          <Shell user={user} onLogout={onLogout} />
        </ToastProvider>
      </ScanProvider>
    </BrandProvider>
  );
}
